import React, { useState, useEffect } from "react";
import { Table, Form, Button, Card, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import "./estimated-budget-ceiling.css";

interface EstimatedBudgetCeilingData {
  id?: number;
  project: string;
  cost_code: string;
  task_name: string;
  type_of_study: string;
  budget: number;
  created_at?: Date;
  updated_at?: Date;
}

interface ParsedTaskInfo {
  cost_code: string;
  task_name: string;
  type_of_study: string;
}

interface ProjectOption {
  job_number: string;
}

interface EstimatedBudgetCeilingProps {
  apiKey: string;
}

const EstimatedBudgetCeiling: React.FC<EstimatedBudgetCeilingProps> = ({
  apiKey,
}) => {
  const [data, setData] = useState<
    (EstimatedBudgetCeilingData | ParsedTaskInfo)[]
  >([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [editingCell, setEditingCell] = useState<{
    cost_code: string;
    task_name: string;
  } | null>(null);
  const [tempValue, setTempValue] = useState<string>("");
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [loading, setLoading] = useState<boolean>(false);
  const [projectsLoading, setProjectsLoading] = useState<boolean>(true);

  // Load projects on component mount
  useEffect(() => {
    if (apiKey) {
      loadProjects();
    } else {
      setProjectsLoading(false);
      setProjects([]);
      setSelectedProject("");
    }
  }, [apiKey]);

  // Load budget ceiling data when project changes
  useEffect(() => {
    if (selectedProject && apiKey) {
      loadBudgetCeilingData();
    }
  }, [selectedProject, apiKey]);

  const loadProjects = async () => {
    if (!apiKey) {
      setProjectsLoading(false);
      return;
    }

    try {
      setProjectsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}estimated-budget-ceiling/projects`,
        {
          headers: { Authorization: apiKey },
          validateStatus: () => true,
        }
      );

      if (response.status === 200) {
        setProjects(response.data);
        if (response.data.length > 0) {
          setSelectedProject(response.data[0].job_number);
        }
      } else {
        console.error("Failed to load projects");
      }
    } catch (error) {
      console.error("Error loading projects:", error);
    } finally {
      setProjectsLoading(false);
    }
  };

  const loadBudgetCeilingData = async () => {
    if (!apiKey || !selectedProject) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}estimated-budget-ceiling/with-parsed-tasks`,
        {
          params: { project: selectedProject },
          headers: { Authorization: apiKey },
          validateStatus: () => true,
        }
      );

      if (response.status === 200) {
        setData(response.data);
      } else {
        console.error("Failed to load budget ceiling data");
        setData([]);
      }
    } catch (error) {
      console.error("Error loading budget ceiling data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (cost_code: string, task_name: string) => {
    setEditingCell({ cost_code, task_name });
    const currentData = data.find(
      (item) => item.cost_code === cost_code && item.task_name === task_name
    );
    setTempValue(
      currentData && "budget" in currentData
        ? currentData.budget.toString()
        : "0"
    );
  };

  const handleCellChange = (value: string) => {
    setTempValue(value);
  };

  const handleCellSave = async () => {
    if (editingCell && selectedProject) {
      const numericValue = parseFloat(tempValue) || 0;
      const taskData = data.find(
        (item) =>
          item.cost_code === editingCell.cost_code &&
          item.task_name === editingCell.task_name
      );

      if (!taskData) return;

      try {
        setSaveStatus("saving");

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}estimated-budget-ceiling`,
          {
            project: selectedProject,
            cost_code: editingCell.cost_code,
            task_name: editingCell.task_name,
            type_of_study: taskData.type_of_study,
            budget: numericValue,
          },
          {
            headers: {
              Authorization: apiKey,
              "Content-Type": "application/json",
            },
            validateStatus: () => true,
          }
        );

        if (response.status === 200 || response.status === 201) {
          // Update local data
          const newData = [...data];
          const existingIndex = newData.findIndex(
            (item) =>
              item.cost_code === editingCell.cost_code &&
              item.task_name === editingCell.task_name
          );

          if (existingIndex >= 0) {
            // Update existing entry with budget
            newData[existingIndex] = {
              ...newData[existingIndex],
              project: selectedProject,
              budget: numericValue,
            } as EstimatedBudgetCeilingData;
          }

          setData(newData);
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus("idle"), 2000);
        } else {
          throw new Error("Failed to save data");
        }
      } catch (error) {
        console.error("Error saving data:", error);
        setSaveStatus("error");
        setTimeout(() => setSaveStatus("idle"), 3000);
      }

      setEditingCell(null);
      setTempValue("");
    }
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setTempValue("");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleCellSave();
    } else if (e.key === "Escape") {
      handleCellCancel();
    }
  };

  const renderCell = (cost_code: string, task_name: string) => {
    const currentData = data.find(
      (item) => item.cost_code === cost_code && item.task_name === task_name
    );
    const value =
      currentData && "budget" in currentData ? currentData.budget : 0;
    const isEditing =
      editingCell?.cost_code === cost_code &&
      editingCell?.task_name === task_name;

    if (isEditing) {
      return (
        <Form.Control
          type="number"
          step="0.001"
          min="0"
          value={tempValue}
          onChange={(e) => handleCellChange(e.target.value)}
          onBlur={handleCellSave}
          onKeyDown={handleKeyPress}
          autoFocus
          className="budget-input"
        />
      );
    }

    return (
      <div
        className="budget-cell"
        onClick={() => handleCellClick(cost_code, task_name)}
        title="Click to edit"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleCellClick(cost_code, task_name);
          }
        }}>
        {value.toFixed(3)}
      </div>
    );
  };

  const getCategoryName = (cost_code: string) => {
    switch (cost_code) {
      case "01":
        return "Project Start-up";
      case "02":
        return "Inception Activities";
      case "03":
        return "Examination";
      case "04":
        return "General Inspections";
      case "05":
        return "Detailed Studies";
      case "06":
        return "Forensic Studies";
      case "07":
        return "Databases and Overall Review Activities";
      case "08":
        return "Project Management";
      case "09":
        return "Other Assignments";
      default:
        return "Unknown";
    }
  };

  const getCategoryColor = (cost_code: string) => {
    switch (cost_code) {
      case "01":
        return "#fff3e0"; // Light orange
      case "02":
        return "#fce4ec"; // Light pink
      case "03":
        return "#e0f2f1"; // Light teal
      case "04":
        return "#e3f2fd"; // Light blue
      case "05":
        return "#f3e5f5"; // Light purple
      case "06":
        return "#fff8e1"; // Light amber
      case "07":
        return "#f1f8e9"; // Light light green
      case "08":
        return "#fafafa"; // Light grey
      case "09":
        return "#e8f5e8"; // Light green
      default:
        return "#f5f5f5";
    }
  };

  if (!apiKey) {
    return (
      <Card className="budget-ceiling-container">
        <Card.Body className="text-center">
          <div className="p-4">
            <h5>Authentication Required</h5>
            <p className="text-muted">
              Please authenticate to access estimated budget ceiling data.
            </p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (projectsLoading) {
    return (
      <Card className="budget-ceiling-container">
        <Card.Body className="text-center">
          <Spinner animation="border" />
          <div className="mt-2">Loading projects...</div>
        </Card.Body>
      </Card>
    );
  }

  // Group data by cost code
  const groupedData = data.reduce((acc, item) => {
    if (!acc[item.cost_code]) {
      acc[item.cost_code] = [];
    }
    acc[item.cost_code].push(item);
    return acc;
  }, {} as Record<string, typeof data>);

  return (
    <Card className="budget-ceiling-container">
      <Card.Header>
        <h5>Estimated Budget Ceiling (HK$ M)</h5>
      </Card.Header>

      <Card.Body>
        <div className="project-controls">
          <div>
            <Form.Label>Project:</Form.Label>
            <Form.Select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="project-selector"
              disabled={projects.length === 0}>
              {projects.map((project) => (
                <option key={project.job_number} value={project.job_number}>
                  {project.job_number}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="action-buttons">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={loadBudgetCeilingData}
              disabled={loading || !selectedProject}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Loading...
                </>
              ) : (
                "Reload"
              )}
            </Button>

            <div className="save-status">
              {saveStatus === "saving" && (
                <>
                  <Spinner animation="border" size="sm" />
                  <span>Saving...</span>
                </>
              )}

              {saveStatus === "saved" && (
                <Alert variant="success" className="mb-0 py-1 px-2">
                  Saved!
                </Alert>
              )}

              {saveStatus === "error" && (
                <Alert variant="danger" className="mb-0 py-1 px-2">
                  Error saving
                </Alert>
              )}
            </div>
          </div>
        </div>

        {selectedProject && (
          <div
            className={`budget-table-container ${
              loading ? "loading-overlay" : ""
            }`}>
            {Object.keys(groupedData)
              .sort()
              .map((costCode) => (
                <div key={costCode} className="category-section">
                  <h6
                    className="category-header"
                    style={{ backgroundColor: getCategoryColor(costCode) }}>
                    {costCode} - {getCategoryName(costCode)}
                    <span className="category-count">
                      ({groupedData[costCode].length} items)
                    </span>
                  </h6>

                  <Table bordered className="budget-table">
                    <thead>
                      <tr>
                        <th>Cost Code</th>
                        <th>Task Name / Location</th>
                        <th>Type of Study</th>
                        <th>Estimated Budget Ceiling (HK$ M)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedData[costCode].map((item, index) => (
                        <tr
                          key={`${item.cost_code}-${item.task_name}-${index}`}>
                          <td className="cost-code-cell">{item.cost_code}</td>
                          <td className="task-name-cell">{item.task_name}</td>
                          <td className="type-study-cell">
                            {item.type_of_study}
                          </td>
                          <td className="budget-value-cell">
                            {renderCell(item.cost_code, item.task_name)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ))}

            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 10,
                }}>
                <Spinner animation="border" />
              </div>
            )}
          </div>
        )}

        <div className="mt-3">
          <small className="text-muted">
            Click on any budget ceiling cell to edit values. Press Enter to save
            or Escape to cancel. Data is automatically saved when you finish
            editing each cell. Budget values are in HK$ Million.
          </small>
        </div>
      </Card.Body>
    </Card>
  );
};

export default EstimatedBudgetCeiling;
